/**
 * Styled XLSX export for the items list.
 *
 * Mirrors `invoices/list/list-export.ts` — info block at the top
 * (title, active tab, active filters, generated date), frozen header
 * row so long lists stay scannable, column widths tuned per field,
 * bold headers with a light-grey fill. Money cells are stored as real
 * rupee numbers with Indian lakh grouping so Excel's SUM / SORT work.
 *
 * Serialises the currently rendered `ListRow[]` (already tab + filter +
 * search narrowed by the list page).
 *
 * Permission is enforced at the tRPC layer via
 * `permissionGate("items.view")` on `items.list` — a user that can't
 * load the list can't export it. ExcelJS is ~700 KB, so we
 * `await import("exceljs")` lazily — only users who click Export pay
 * the download.
 */
import type { items as itemsContract } from "@flint/api-contract";
import type { z } from "zod";

type ListRow = z.infer<typeof itemsContract.listRow>;

const INR_FORMAT = '"₹"#,##,##0.00;"-₹"#,##,##0.00';
const PCT_FORMAT = "0.00\"%\"";
const DATE_FORMAT = "yyyy-mm-dd";

const HEADERS = [
  "Name",
  "SKU",
  "Type",
  "Usage",
  "Category",
  "Brand",
  "HSN/SAC",
  "Unit",
  "Reorder level",
  "Last cost (INR)",
  "Rate (INR)",
  "GST %",
  "Margin %",
  "Preferred vendor",
  "Created",
] as const;

/** Column widths tuned so no field renders as `###`. */
const COLUMN_WIDTHS = [32, 16, 10, 10, 18, 18, 12, 8, 14, 16, 16, 8, 10, 24, 12] as const;

const TAB_LABEL: Record<string, string> = {
  all: "All",
  goods: "Goods",
  services: "Services",
  low_stock: "Low stock",
  untagged: "Untagged",
  archived: "Archived",
};

function paiseToRupees(paise: string | null): number | null {
  if (!paise) return null;
  return Number(BigInt(paise)) / 100;
}

function marginPct(ratePaise: string | null, costPaise: string | null): number | null {
  if (!ratePaise || !costPaise) return null;
  const rate = BigInt(ratePaise);
  const cost = BigInt(costPaise);
  if (rate === 0n) return null;
  return Math.round(Number((rate - cost) * 10000n) / Number(rate)) / 100;
}

function capitalize(s: string): string {
  return s[0]!.toUpperCase() + s.slice(1);
}

export interface ExportContext {
  /** Active status tab (`all` / `goods` / ...). Renders in the info block. */
  tab: string;
  /** Free-text search, or empty. */
  search: string;
  /** Category chip label, or `null`. */
  category: string | null;
  /** GST rate filter label, or `null`. */
  gst: string | null;
  /** Vendor chip label, or `null`. */
  vendor: string | null;
}

/**
 * Build an `.xlsx` blob from the visible rows + filter context and
 * trigger a browser download. Filename embeds the tab scope + today.
 */
export async function downloadItemsXlsx(
  rows: ListRow[],
  filename: string,
  ctx: ExportContext,
): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Flint";
  wb.created = new Date();
  const ws = wb.addWorksheet("Items", {
    // Freeze at row 6 so the info block + header row stay visible when
    // scrolling — matches the invoices/customers export pattern.
    views: [{ state: "frozen", ySplit: 6 }],
  });

  ws.columns = COLUMN_WIDTHS.map((width) => ({ width }));

  const lastCol = colLetter(HEADERS.length);
  const dataCol = HEADERS.length;

  // ── Row 1 — Title ─────────────────────────────────────────────────
  ws.mergeCells(`A1:${lastCol}1`);
  const title = ws.getCell("A1");
  title.value = `Items — ${TAB_LABEL[ctx.tab] ?? "All"}`;
  title.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FF0F172A" } };
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 28;

  // ── Rows 2-5 — Info block ─────────────────────────────────────────
  const infoRows: Array<[string, string]> = [
    ["View", TAB_LABEL[ctx.tab] ?? "All"],
    [
      "Filters",
      [
        ctx.search ? `Search "${ctx.search}"` : null,
        ctx.category ? `Category: ${ctx.category}` : null,
        ctx.gst ? `GST: ${ctx.gst}` : null,
        ctx.vendor ? `Vendor: ${ctx.vendor}` : null,
      ]
        .filter(Boolean)
        .join(" · ") || "—",
    ],
    ["Rows", `${rows.length} item${rows.length === 1 ? "" : "s"}`],
    ["Generated", new Date().toISOString().slice(0, 10)],
  ];
  infoRows.forEach(([label, value], i) => {
    const row = ws.getRow(2 + i);
    row.getCell(1).value = label;
    row.getCell(1).font = { bold: true, color: { argb: "FF475569" } };
    row.getCell(2).value = value;
  });

  // ── Row 6 — Data header ───────────────────────────────────────────
  const headerRow = ws.getRow(6);
  HEADERS.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { bold: true, color: { argb: "FF0F172A" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF1F5F9" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = { bottom: { style: "thin", color: { argb: "FF94A3B8" } } };
  });
  headerRow.height = 22;

  // ── Rows 7+ — Data ────────────────────────────────────────────────
  rows.forEach((r, i) => {
    const rowNum = 7 + i;
    const row = ws.getRow(rowNum);

    row.getCell(1).value = r.name;

    row.getCell(2).value = r.sku ?? "";
    row.getCell(2).numFmt = "@"; // text — keep leading zeros

    row.getCell(3).value = capitalize(r.itemType);
    row.getCell(4).value = capitalize(r.usage);
    row.getCell(5).value = r.category ?? "";
    row.getCell(6).value = r.brand ?? "";

    row.getCell(7).value = r.hsn ?? "";
    row.getCell(7).numFmt = "@"; // text — HSN is a code, not a number

    row.getCell(8).value = r.unit;

    // Reorder level — only meaningful when tracking is on.
    if (r.trackStock && r.reorderLevel !== null) {
      row.getCell(9).value = r.reorderLevel;
    } else {
      row.getCell(9).value = "";
    }

    // Money as real numbers with Indian lakh grouping.
    const cost = paiseToRupees(r.lastCostPaise);
    if (cost !== null) {
      row.getCell(10).value = cost;
      row.getCell(10).numFmt = INR_FORMAT;
    }
    const rate = paiseToRupees(r.defaultRatePaise);
    if (rate !== null) {
      row.getCell(11).value = rate;
      row.getCell(11).numFmt = INR_FORMAT;
    }

    if (r.defaultTaxRateBps !== null) {
      row.getCell(12).value = r.defaultTaxRateBps / 100;
      row.getCell(12).numFmt = "0.00";
    }

    const margin = marginPct(r.defaultRatePaise, r.lastCostPaise);
    if (margin !== null) {
      row.getCell(13).value = margin;
      row.getCell(13).numFmt = PCT_FORMAT;
    }

    row.getCell(14).value = r.preferredVendorName ?? "";

    // Date as real Date so Excel can sort chronologically.
    const createdDate = new Date(r.createdAt);
    if (!Number.isNaN(createdDate.getTime())) {
      row.getCell(15).value = createdDate;
      row.getCell(15).numFmt = DATE_FORMAT;
    }
  });

  // Autofilter over the data range so users can sort/filter in-app.
  if (rows.length > 0) {
    ws.autoFilter = {
      from: { row: 6, column: 1 },
      to: { row: 6 + rows.length, column: dataCol },
    };
  }

  // ── Download ──────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Excel column letter for a 1-indexed column number (supports up to 702 cols). */
function colLetter(n: number): string {
  if (n <= 26) return String.fromCharCode(64 + n);
  const first = Math.floor((n - 1) / 26);
  const second = ((n - 1) % 26) + 1;
  return `${String.fromCharCode(64 + first)}${String.fromCharCode(64 + second)}`;
}
